import type { ComponentChildren } from 'preact';

/** Turns a presentational design-system block into a real control.
 *
 *  Several handoff components take an `onClick` but render a bare `<div>` or
 *  `<span>` — CaptureBar and TabBar among them. That is defensible for a design
 *  artifact and wrong for a shipped app: the capture bar is the product's
 *  primary action and would be unreachable by keyboard, and unnamed to a
 *  screen reader.
 *
 *  Rather than edit the copied components (which are verbatim so they stay
 *  auditable against the design source), wrap them here. Reported back to the
 *  design system as an accessibility defect; when the components grow real
 *  semantics this wrapper can go.
 *
 *  **It also carries the touch floor.** A thing that can be tapped is a touch
 *  target, and the brief puts the minimum at 44px with the responsive spec
 *  calling it unchanged on iPad — a keyboard does not remove the finger. Left to
 *  the content, a wrapped row is as tall as its text: calendar event rows came
 *  out at 36px and an unwritten Gratitude day at 18px. Setting the floor here
 *  fixes the class rather than the instances, and a caller that genuinely wants
 *  something smaller has to say so in its own `style`.
 */
export function Pressable({
  label,
  onPress,
  pressed,
  children,
  style,
}: {
  label: string;
  onPress: () => void;
  pressed?: boolean;
  children: ComponentChildren;
  style?: Record<string, string | number>;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={pressed}
      onClick={onPress}
      style={{
        display: 'block',
        width: '100%',
        minHeight: 'var(--bujo-row-min)',
        textAlign: 'inherit',
        font: 'inherit',
        color: 'inherit',
        background: 'none',
        border: 0,
        padding: 0,
        cursor: 'pointer',
        ...style,
      }}
    >
      {children}
    </button>
  );
}
